import type {
    ComponentParams,
    ComponentRendering,
    Field,
    ImageField,
    LinkField,
} from "@sitecore-jss/sitecore-jss-nextjs";
import { useSitecoreContext } from "@sitecore-jss/sitecore-jss-nextjs";
import { Box, Flex } from "@chakra-ui/react";
import type { GenericHeaderProps } from "commons/ui/GenericHeader";
import GenericHeader from "commons/ui/GenericHeader";
import BundleIcon from "commons/ui/BundleIcon";
import SCHeading from "commons/sc/SCHeading";
import SCText from "commons/sc/SCText";
import SCLinkButton from "commons/sc/SCLinkButton";
import type { CookieConsentFieldProps } from "commons/ui/CookieConsent";
import VideoPlayer from "commons/ui/VideoPlayer";

interface Fields extends GenericHeaderProps, CookieConsentFieldProps {
    Title: Field<string>;
    Text: Field<string>;
    CTA: LinkField;
    ThumbnailImage: ImageField;
    ThumbnailUrl: Field<string>;
    VideoUrl: Field<string>;
}

export type VideoProps = {
    rendering: ComponentRendering & { params: ComponentParams };
    params: { [key: string]: string };
    fields: Fields;
};

type VideoComponentProps = {
    fields: Fields;
    pageEditing?: boolean;
    renderVideoPlayer?: boolean;
};

const VideoContentOnTop = ({ fields, pageEditing }: VideoComponentProps) => {
    if (!pageEditing) return null;

    return (
        <Flex flexDirection="column" gap="sp16" mb="sp16">
            <Flex flexDirection="column">
                Thumbnail URL: (Leave empty to choose ThumbnailImage)
                <SCText
                    textField={fields?.ThumbnailUrl}
                    border="1px solid black"
                    mt="sp12"
                    wordBreak="break-word"
                />
            </Flex>
            <Flex flexDirection="column">
                Video URL:
                <SCText
                    textField={fields?.VideoUrl}
                    border="1px solid black"
                    mt="sp12"
                    wordBreak="break-word"
                />
            </Flex>
        </Flex>
    );
};

const VideoContentOnBottom = ({ fields, pageEditing, renderVideoPlayer }: VideoComponentProps) => {
    const hasContent =
        fields.Title.value !== "" ||
        fields.Text.value !== "" ||
        fields.CTA.value.href !== "" ||
        pageEditing;

    if (!hasContent) return null;

    return (
        <Flex
            {...(renderVideoPlayer && { mt: "sp48" })}
            flexDirection={["column", null, null, "row"]}
        >
            {(fields?.Title.value !== "" || pageEditing) && (
                <Box flex={1}>
                    <SCHeading as="h3" size="sm" textField={fields?.Title} />
                </Box>
            )}

            {(fields?.Text.value !== "" || fields?.CTA.value.href !== "" || pageEditing) && (
                <Box flex={1} mt={["sp16", null, null, 0]}>
                    {fields?.Text.value !== "" && <SCText textField={fields?.Text} />}

                    {(fields?.CTA.value.href !== "" || pageEditing) && (
                        <SCLinkButton
                            linkField={fields?.CTA}
                            variant="primary"
                            mt="sp32"
                            rightIcon={<BundleIcon name="ArrowForward" />}
                        />
                    )}
                </Box>
            )}
        </Flex>
    );
};

const VideoPlayerComponent = ({ fields }: VideoComponentProps) => {
    const cookieConsentFields = {
        AnalyticsCookies: fields?.AnalyticsCookies,
        FunctionalCookies: fields?.FunctionalCookies,
        MarketingCookies: fields?.MarketingCookies,
    };

    const thumbnail = {
        thumbnailAlt: fields?.Title.value,
        thumbnailImage: fields?.ThumbnailImage,
        thumbnailUrl: fields?.ThumbnailUrl.value,
    };

    return (
        <VideoPlayer
            cookies={cookieConsentFields}
            videoUrl={fields?.VideoUrl?.value}
            thumbnail={thumbnail}
        />
    );
};

const CONTAINER_PADDING = "clamp(1.5rem, -1.493rem + 12.77vw, 10rem)";

const VideoComponent = (props: VideoProps & { isFullWidth?: boolean }) => {
    const {
        sitecoreContext: { pageEditing },
    } = useSitecoreContext();

    const renderVideoPlayer =
        props.fields?.VideoUrl?.value !== "" ||
        props.fields?.ThumbnailUrl?.value !== "" ||
        props.fields?.ThumbnailImage?.value?.src !== "" ||
        pageEditing;

    if (!props.fields) return null;

    const videoPlayerContent = (
        <VideoPlayerComponent fields={props.fields} pageEditing={pageEditing} />
    );

    const fullWidthStyles = props.isFullWidth
        ? {
              width: `calc(100% + 2 * ${CONTAINER_PADDING})`,
              marginLeft: `calc(-1 * ${CONTAINER_PADDING})`,
              marginRight: `calc(-1 * ${CONTAINER_PADDING})`,
          }
        : {};

    return (
        <GenericHeader
            Id={props.rendering?.uid ?? props.rendering?.componentName}
            AnchorId={props.fields?.AnchorId}
            HeaderHeadline={props.fields?.HeaderHeadline}
            HeaderSubHeadline={props.fields?.HeaderSubHeadline}
            HeaderCTA={props.fields?.HeaderCTA}
            headlineSize={props.params?.HeaderHeadlineSize}
            spacingSize={props.params?.Padding}
        >
            <VideoContentOnTop fields={props.fields} pageEditing={pageEditing} />
            {props.isFullWidth ? (
                <Box {...fullWidthStyles}>{videoPlayerContent}</Box>
            ) : (
                videoPlayerContent
            )}
            <VideoContentOnBottom
                fields={props.fields}
                pageEditing={pageEditing}
                renderVideoPlayer={renderVideoPlayer}
            />
        </GenericHeader>
    );
};

export const Default = (props: VideoProps) => <VideoComponent {...props} />;

export const FullWidth = (props: VideoProps) => <VideoComponent {...props} isFullWidth />;
